import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  PhoneIcon,
  XMarkIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import NewTicketModal from "../pages/home/Administration/Tickets/ViewTickets/NewTicketModal/NewTicketModal";
import ContactEditorModal from "./modals/ContactEditorModal";
import petition_user from "../pages/home/petitions/petition_user";
import petition_post from "../pages/home/petitions/petition_post";
import path_user from "../pages/home/petitions/petition_patch";

export default function IncomingCallPopup({
  isOpen,
  callerNumber,
  callerName,
  contact,
  account,
  recentTickets,
  uniqueid,
  onClose,
}) {
  const history = useHistory();
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [visible, setVisible] = useState(false);

  // Account search state for unknown callers
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const searchTimeout = useRef(null);

  // Animate in when popup opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedAccount(null);
      setShowNewTicket(false);
      setShowNewContact(false);
      // Small delay so the animation triggers after mount
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Use matched account or manually selected one
  const activeAccount = account || selectedAccount;
  const isUnknown = !contact && !account;

  const handleCreateTicket = () => {
    setShowNewTicket(true);
  };

  const handleTicketCreated = () => {
    setShowNewTicket(false);
    onClose();
  };

  const handleViewTicket = (ticketPk) => {
    onClose();
    history.push(`/TicketDetails?pk=${ticketPk}`);
  };

  const handleViewAccount = () => {
    if (activeAccount?.pk) {
      onClose();
      history.push(`/AccountDetails?pk=${activeAccount.pk}`);
    }
  };

  const handleCreateContact = () => {
    setShowNewContact(true);
  };

  // Clean the caller number to just digits for the mobile field
  const cleanPhone = (num) => {
    if (!num) return "";
    return num.replace(/[^\d]/g, "").replace(/^1(\d{10})$/, "$1");
  };

  const handleContactSave = async (data, existingContact) => {
    if (existingContact) {
      await path_user("editContacts", existingContact.pk, data);
    } else {
      await petition_post("newContacts", data);
    }
    setShowNewContact(false);
  };

  const handleAccountSearch = (query) => {
    setSearchQuery(query);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await petition_user(
          "coustomerLookUp_account_name",
          query
        );
        const results = data?.result || [];
        setSearchResults(
          (Array.isArray(results) ? results : []).slice(0, 5)
        );
      } catch (err) {
        console.error("Account search error:", err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleSelectAccount = (acct) => {
    setSelectedAccount({ pk: acct.pk, name: acct.name });
    setSearchQuery("");
    setSearchResults([]);
  };

  const displayName = callerName || "Unknown Caller";
  const glowClass = isUnknown && !selectedAccount
    ? "tw-animate-glow-amber"
    : "tw-animate-glow";
  const headerBg = isUnknown && !selectedAccount
    ? "tw-bg-gradient-to-r tw-from-amber-500 tw-to-orange-500"
    : "tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-600";

  return (
    <>
      {/* Backdrop overlay - hidden when sub-modals are open so they aren't covered */}
      {!showNewTicket && !showNewContact && (
        <div
          className={`tw-fixed tw-inset-0 tw-z-[9998] tw-bg-black/20 tw-backdrop-blur-[2px] tw-transition-opacity tw-duration-500 ${
            visible ? "tw-opacity-100" : "tw-opacity-0"
          }`}
          onClick={onClose}
        />
      )}

      {/* Main popup container */}
      <div
        className={`tw-fixed tw-top-8 tw-right-8 tw-z-[9999] tw-w-[440px] ${
          visible ? "tw-animate-slide-down" : "tw-opacity-0 tw--translate-y-full"
        }`}
      >
        <div
          className={`tw-rounded-2xl tw-bg-white dark:tw-bg-gray-800 tw-shadow-2xl tw-ring-1 tw-ring-gray-200 dark:tw-ring-gray-700 ${glowClass}`}
        >
          {/* Header - larger with animated phone icon */}
          <div className={`tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between tw-rounded-t-2xl ${headerBg}`}>
            <div className="tw-flex tw-items-center tw-gap-4">
              {/* Animated phone icon with pulse rings */}
              <div className="tw-relative">
                <div className="tw-absolute tw-inset-0 tw-rounded-full tw-bg-white/30 tw-animate-pulse-ring" />
                <div className="tw-relative tw-w-12 tw-h-12 tw-rounded-full tw-bg-white/25 tw-flex tw-items-center tw-justify-center tw-backdrop-blur-sm">
                  <PhoneIcon className="tw-w-6 tw-h-6 tw-text-white tw-animate-phone-ring" />
                </div>
              </div>
              <div>
                <p className="tw-text-base tw-font-bold tw-text-white tw-tracking-wide">
                  Incoming Call
                </p>
                <p className="tw-text-sm tw-text-white/90 tw-font-medium tw-tracking-wider">
                  {callerNumber}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="tw-rounded-full tw-p-1.5 tw-text-white/70 hover:tw-bg-white/20 hover:tw-text-white tw-transition-colors"
            >
              <XMarkIcon className="tw-w-6 tw-h-6" />
            </button>
          </div>

          {/* Caller Info */}
          <div className="tw-px-6 tw-py-5">
            <div className="tw-flex tw-items-center tw-gap-4 tw-mb-5">
              <div
                className={`tw-w-16 tw-h-16 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${
                  isUnknown && !selectedAccount
                    ? "tw-bg-amber-100 dark:tw-bg-amber-900/50"
                    : "tw-bg-blue-100 dark:tw-bg-blue-900/50"
                }`}
              >
                <UserCircleIcon
                  className={`tw-w-10 tw-h-10 ${
                    isUnknown && !selectedAccount
                      ? "tw-text-amber-600 dark:tw-text-amber-400"
                      : "tw-text-blue-600 dark:tw-text-blue-400"
                  }`}
                />
              </div>
              <div>
                <p className="tw-text-xl tw-font-bold tw-text-gray-900 dark:tw-text-white">
                  {displayName}
                </p>
                {contact?.email && (
                  <p className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                    {contact.email}
                  </p>
                )}
                {activeAccount && (
                  <button
                    onClick={handleViewAccount}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-blue-600 dark:tw-text-blue-400 hover:tw-underline tw-mt-0.5"
                  >
                    <BuildingOfficeIcon className="tw-w-4 tw-h-4" />
                    {activeAccount.name}
                  </button>
                )}
                {isUnknown && !selectedAccount && (
                  <p className="tw-text-sm tw-font-medium tw-text-amber-600 dark:tw-text-amber-400 tw-mt-0.5">
                    No matching contact found
                  </p>
                )}
              </div>
            </div>

            {/* Account Search - shown for unknown callers without a selected account */}
            {isUnknown && !selectedAccount && (
              <div className="tw-mb-5">
                <p className="tw-text-xs tw-font-semibold tw-text-gray-500 dark:tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-2">
                  Link to Account
                </p>
                <div className="tw-relative">
                  <MagnifyingGlassIcon className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleAccountSearch(e.target.value)}
                    placeholder="Search account name..."
                    autoFocus
                    className="tw-w-full tw-pl-10 tw-pr-4 tw-py-2.5 tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-text-sm tw-text-gray-900 dark:tw-text-white focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-shadow"
                  />
                </div>
                {/* Search Results */}
                {(searchResults.length > 0 || searching) && (
                  <div className="tw-mt-2 tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-overflow-hidden tw-shadow-lg">
                    {searching ? (
                      <div className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                        Searching...
                      </div>
                    ) : (
                      searchResults.map((result) => (
                        <button
                          key={result.pk}
                          onClick={() => handleSelectAccount(result)}
                          className="tw-w-full tw-text-left tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 dark:tw-text-white hover:tw-bg-blue-50 dark:hover:tw-bg-blue-900/30 tw-transition-colors tw-border-b tw-border-gray-100 dark:tw-border-gray-600 last:tw-border-b-0"
                        >
                          <span className="tw-font-medium">{result.name}</span>
                          {result.account_number && (
                            <span className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-ml-2">
                              #{result.account_number}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Selected account badge for unknown callers */}
            {isUnknown && selectedAccount && (
              <div className="tw-mb-5 tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                  Linked to:
                </span>
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-full tw-bg-blue-100 dark:tw-bg-blue-900/50 tw-text-sm tw-font-medium tw-text-blue-700 dark:tw-text-blue-300">
                  <BuildingOfficeIcon className="tw-w-3.5 tw-h-3.5" />
                  {selectedAccount.name}
                </span>
                <button
                  onClick={() => setSelectedAccount(null)}
                  className="tw-text-xs tw-text-gray-400 hover:tw-text-gray-600 dark:hover:tw-text-gray-300 tw-underline"
                >
                  change
                </button>
              </div>
            )}

            {/* Recent Open Tickets */}
            {recentTickets.length > 0 && (
              <div className="tw-mb-5">
                <p className="tw-text-xs tw-font-semibold tw-text-gray-500 dark:tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-2">
                  Open Tickets ({recentTickets.length})
                </p>
                <div className="tw-space-y-2 tw-max-h-44 tw-overflow-y-auto tw-pr-1">
                  {recentTickets.map((ticket) => (
                    <button
                      key={ticket.pk}
                      onClick={() => handleViewTicket(ticket.pk)}
                      className="tw-w-full tw-text-left tw-px-4 tw-py-3 tw-rounded-xl tw-bg-gray-50 dark:tw-bg-gray-700/50 hover:tw-bg-blue-50 dark:hover:tw-bg-blue-900/30 tw-transition-all tw-duration-200 tw-group hover:tw-shadow-md hover:tw-scale-[1.01]"
                    >
                      <div className="tw-flex tw-items-center tw-justify-between">
                        <span className="tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white group-hover:tw-text-blue-600 dark:group-hover:tw-text-blue-400 tw-truncate tw-max-w-[240px]">
                          #{ticket.pk} - {ticket.title || "No title"}
                        </span>
                        <span
                          className={`tw-text-xs tw-px-2.5 tw-py-1 tw-rounded-full tw-font-semibold tw-flex-shrink-0 ${
                            ticket.priority === "critical"
                              ? "tw-bg-red-100 tw-text-red-700 dark:tw-bg-red-900/50 dark:tw-text-red-300"
                              : ticket.priority === "high"
                              ? "tw-bg-orange-100 tw-text-orange-700 dark:tw-bg-orange-900/50 dark:tw-text-orange-300"
                              : "tw-bg-gray-100 tw-text-gray-600 dark:tw-bg-gray-600 dark:tw-text-gray-300"
                          }`}
                        >
                          {ticket.status}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions - bigger buttons */}
            <div className="tw-flex tw-gap-3">
              <button
                onClick={handleCreateTicket}
                className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-5 tw-py-3 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-font-semibold tw-text-sm hover:tw-bg-blue-600 tw-transition-all tw-duration-200 hover:tw-shadow-lg hover:tw-shadow-blue-500/25 hover:tw-scale-[1.02] active:tw-scale-[0.98]"
              >
                <PlusIcon className="tw-w-5 tw-h-5" />
                New Ticket
              </button>
              {activeAccount && (
                <button
                  onClick={handleViewAccount}
                  className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-5 tw-py-3 tw-rounded-xl tw-bg-gray-100 dark:tw-bg-gray-700 tw-text-gray-700 dark:tw-text-gray-300 tw-font-semibold tw-text-sm hover:tw-bg-gray-200 dark:hover:tw-bg-gray-600 tw-transition-all tw-duration-200 hover:tw-shadow-lg hover:tw-scale-[1.02] active:tw-scale-[0.98]"
                >
                  <BuildingOfficeIcon className="tw-w-5 tw-h-5" />
                  View Account
                </button>
              )}
            </div>

            {/* Create Contact link - for unknown callers */}
            {isUnknown && (
              <button
                onClick={handleCreateContact}
                className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mt-3 tw-px-5 tw-py-3 tw-rounded-xl tw-text-sm tw-font-semibold tw-text-purple-600 dark:tw-text-purple-400 tw-border-2 tw-border-dashed tw-border-purple-300 dark:tw-border-purple-700 hover:tw-bg-purple-50 dark:hover:tw-bg-purple-900/20 tw-transition-all tw-duration-200 hover:tw-border-purple-400 hover:tw-scale-[1.02] active:tw-scale-[0.98]"
              >
                <UserPlusIcon className="tw-w-5 tw-h-5" />
                Create New Contact
              </button>
            )}
          </div>
        </div>
      </div>

      {/* New Ticket Modal - pre-filled with account */}
      <NewTicketModal
        show={showNewTicket}
        onHide={() => setShowNewTicket(false)}
        onSuccess={handleTicketCreated}
        accountId={activeAccount?.pk || null}
      />

      {/* Create Contact Modal - pre-filled with caller phone */}
      <ContactEditorModal
        isOpen={showNewContact}
        onClose={() => setShowNewContact(false)}
        contact={null}
        accountId={activeAccount?.pk}
        onSave={handleContactSave}
        defaultValues={{ mobile: cleanPhone(callerNumber) }}
      />
    </>
  );
}
